import React from 'react';

import LinkIcon from '@atlaskit/icon/core/link';
import { AnyAri } from '@atlassian/ari/any-ari';
import {
	isBranchEntity,
	isCalendarEventEntity,
	isCommitEntity,
	isConversationEntity,
	isDesignEntity,
	isDocumentEntity,
	isMessageEntity,
	isPullRequestEntity,
	isRepositoryEntity,
	isVideoEntity,
	isWorkItemEntity,
	type SearchResultEntity,
	type SearchResultGraphDocument,
} from '@atlassian/dynamic-cards-search-client';

import { ProductKeys, products } from '../../../../../../common/constants/products';
import { getIntegrationKey } from '../../../../../../common/utils/search-result-utils';
import { useBootstrap } from '../../../../../../controllers/store';
import { BranchSearchResult } from './branch-search-result';
import { CalendarEventSearchResult } from './calendar-event-search-result';
import { CommitSearchResult } from './commit-search-result';
import { ConversationSearchResult } from './conversation-search-result';
import { DefaultGraphResult } from './default-graph-result';
import { DesignSearchResult } from './design-search-result';
import { DocumentSearchResult } from './document-search-result';
import { MessageSearchResult } from './message-search-result';
import { PullRequestSearchResult } from './pull-request-search-result';
import { RepositorySearchResult } from './repository-search-result';
import { VideoSearchResult } from './video-search-result';
import { WorkItemSearchResult } from './work-item-search-result';

const MICROSOFT_INTEGRATION_KEY = 'microsoft';
const SHAREPOINT_SUPPORTED_RESOURCE_TYPES = ['document'];
const TEAMS_SUPPORTED_RESOURCE_TYPES = ['message', 'conversation'];

interface ThirdPartySearchResultProps {
	partial: SearchResultGraphDocument<SearchResultEntity>;
	/** The position in the results list shown to the user. */
	position?: number;
}

const getIcon = ({
	integrationKey,
	resourceType,
}: {
	integrationKey?: string;
	resourceType: string;
}) => {
	if (!integrationKey) {
		return <LinkIcon color="currentColor" spacing="spacious" label="" />;
	}

	let productKey = integrationKey === 'google' ? ProductKeys.GoogleDrive : integrationKey;
	/**
	 * Temporary fix for Microsoft integrationKey
	 */
	if (
		integrationKey === MICROSOFT_INTEGRATION_KEY &&
		SHAREPOINT_SUPPORTED_RESOURCE_TYPES.includes(resourceType)
	) {
		productKey = ProductKeys.Sharepoint;
	} else if (
		integrationKey === MICROSOFT_INTEGRATION_KEY &&
		TEAMS_SUPPORTED_RESOURCE_TYPES.includes(resourceType)
	) {
		productKey = ProductKeys.Teams;
	}

	let IconComponent = products[productKey]?.SearchResultIcon || products[productKey]?.Icon;
	return IconComponent ? (
		<IconComponent appearance="brand" label="" />
	) : (
		<LinkIcon color="currentColor" spacing="spacious" label="" />
	);
};

export const ThirdPartySearchResult = ({
	partial,
	position,
}: ThirdPartySearchResultProps): React.JSX.Element => {
	const [{ cloudId }] = useBootstrap();
	const resultAri = AnyAri.parse(partial.id);
	const integrationKey = getIntegrationKey(partial.integrationId);

	const icon = getIcon({ integrationKey, resourceType: resultAri.resourceType });

	if (isConversationEntity(partial)) {
		return (
			<ConversationSearchResult
				partial={partial}
				position={position}
				icon={icon}
				integrationKey={integrationKey}
				resultAri={resultAri}
				cloudId={cloudId}
			/>
		);
	}
	if (isDesignEntity(partial)) {
		return <DesignSearchResult partial={partial} position={position} icon={icon} />;
	}

	if (isBranchEntity(partial)) {
		return <BranchSearchResult partial={partial} position={position} icon={icon} />;
	}

	if (isCommitEntity(partial)) {
		return <CommitSearchResult partial={partial} position={position} icon={icon} />;
	}

	if (isPullRequestEntity(partial)) {
		return <PullRequestSearchResult partial={partial} position={position} icon={icon} />;
	}

	if (isRepositoryEntity(partial)) {
		return <RepositorySearchResult partial={partial} position={position} icon={icon} />;
	}

	if (isVideoEntity(partial)) {
		return <VideoSearchResult partial={partial} position={position} icon={icon} />;
	}

	if (isMessageEntity(partial)) {
		return <MessageSearchResult partial={partial} position={position} icon={icon} />;
	}

	if (isDocumentEntity(partial)) {
		return (
			<DocumentSearchResult
				partial={partial}
				position={position}
				resultAri={resultAri}
				icon={icon}
				integrationKey={integrationKey}
			/>
		);
	}

	if (isCalendarEventEntity(partial)) {
		return (
			<CalendarEventSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={integrationKey}
			/>
		);
	}

	if (isWorkItemEntity(partial)) {
		return (
			<WorkItemSearchResult
				partial={partial}
				position={position}
				icon={icon}
				product={integrationKey}
			/>
		);
	}

	return (
		<DefaultGraphResult
			partial={partial as SearchResultGraphDocument<SearchResultEntity>}
			position={position}
			resultAri={resultAri}
			icon={icon}
			integrationKey={integrationKey}
		/>
	);
};
